import { defineStore } from 'pinia';
import { ref } from 'vue';
import { reqUpdateWidgets } from 'src/api/user';
import { canAddWidgetType, getWidgetDefinition } from 'src/services/WidgetConfiguration';
import type { DashboardWidgetConfig, DashboardWidgetInstance, DashboardWidgetPlacement } from 'types/app/dashboard';

export const useWidgetsStore = defineStore('widgets', () => {
  const widgets = ref<DashboardWidgetInstance[]>([]);

  function initWidgets(storedWidgets: DashboardWidgetInstance[] | null | undefined) {
    widgets.value = (storedWidgets ?? []).filter((widget) => Boolean(getWidgetDefinition(widget.type)));
  }

  function resetWidgets() {
    widgets.value = [];
  }

  let saveLock: Promise<void> = Promise.resolve();

  async function saveWidgets() {
    const previousSave = saveLock;
    let releaseLock = () => {};
    saveLock = new Promise((resolve) => {
      releaseLock = resolve;
    });

    await previousSave;
    try {
      await reqUpdateWidgets(widgets.value);
    } finally {
      releaseLock();
    }
  }

  async function updateWidgetConfig(id: string, config: DashboardWidgetConfig) {
    const widget = widgets.value.find((entry) => entry.id === id);
    if (!widget) return;
    widget.config = config;
    await saveWidgets();
  }

  async function updateWidgetPlacement(id: string, placement: DashboardWidgetPlacement) {
    const widget = widgets.value.find((entry) => entry.id === id);
    if (!widget) return;

    const nextX = placement.x ?? widget.x;
    const nextY = placement.y ?? widget.y;
    const nextW = placement.w ?? widget.w;
    const nextH = placement.h ?? widget.h;

    const hasChanges = widget.x !== nextX || widget.y !== nextY || widget.w !== nextW || widget.h !== nextH;
    if (!hasChanges) return;

    widget.x = nextX;
    widget.y = nextY;
    widget.w = nextW;
    widget.h = nextH;
    await saveWidgets();
  }

  async function addWidget(widget: DashboardWidgetInstance) {
    if (!canAddWidgetType(widget.type, widgets.value)) return false;
    widgets.value = [...widgets.value, widget];
    await saveWidgets();
    return true;
  }

  async function removeWidget(id: string) {
    widgets.value = widgets.value.filter((widget) => widget.id !== id);
    await saveWidgets();
  }

  return {
    widgets,
    initWidgets,
    resetWidgets,
    saveWidgets,
    updateWidgetConfig,
    updateWidgetPlacement,
    addWidget,
    removeWidget
  };
});
